<template>
  <AppHeaderBack>Delete Account</AppHeaderBack>
  <div class="min-h-screen bg-gray-100 pt-8 pb-8 px-4">
    <div class="max-w-3xl mx-auto">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="p-4 border-b border-gray-200 flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-xl text-red-600">delete</span>
            <h2 class="text-lg font-semibold text-gray-800">Delete Account</h2>
          </div>
        </div>
        
        <div class="p-6">
          <!-- Initial Delete Button -->
          <div v-if="!showDeleteInput" class="text-center">
            <p class="text-gray-700 mb-6">This will permanently delete your account and all associated data. This action cannot be undone.</p>
            <button
              @click="showDeleteInput = true"
              class="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-200"
            >
              Delete Account
            </button>
          </div>

          <!-- Confirmation Form -->
          <div v-if="showDeleteInput" class="space-y-6">
            <!-- Password input for email/password users -->
            <div v-if="!isGoogleUser">
              <label for="deletePassword" class="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
                  </svg>
                </div>
                <input
                  id="deletePassword"
                  v-model="deletePassword"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Enter your password"
                  required
                  :disabled="loading"
                  class="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 placeholder-gray-400 transition duration-200 disabled:opacity-50"
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    @click="showPassword = !showPassword"
                    class="text-gray-400 hover:text-gray-500 focus:outline-none"
                    :disabled="loading"
                  >
                    <span class="material-symbols-outlined text-xl">
                      {{ showPassword ? 'visibility_off' : 'visibility' }}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Message for Google users -->
            <div v-else class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 mt-0.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <div>
                  <p class="text-sm font-medium text-blue-900">Google Account Sign-In</p>
                  <p class="text-xs text-blue-700 mt-1">You'll need to confirm your Google account to delete your account.</p>
                </div>
              </div>
            </div>

            <div class="flex space-x-4">
              <button
                @click="confirmDeleteAccount"
                :disabled="loading || (!isGoogleUser && !deletePassword)"
                class="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ loading ? "Deleting..." : "Confirm Delete" }}
              </button>

              <button
                @click="cancelDelete"
                :disabled="loading"
                class="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>

            <p class="text-xs text-red-600 text-center mt-4">
              Warning: This action is irreversible. All your data will be permanently deleted.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { deleteAccount } from "@/composables/useAuth";
import { auth } from "@/firebase/init";
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';

export default {
  components: { AppHeaderBack },
  data() {
    return {
      showDeleteInput: false,
      deletePassword: "",
      showPassword: false,
      loading: false,
    };
  },
  computed: {
    isGoogleUser() {
      const user = auth.currentUser;
      if (!user) return false;
      return user.providerData.some(
        provider => provider.providerId === 'google.com'
      );
    }
  },
  methods: {
    async confirmDeleteAccount() {
      // For Google users, no password needed
      if (this.isGoogleUser) {
        this.loading = true;
        try {
          await deleteAccount(null);
          this.$store.dispatch("showAlert", { 
            message: "Account deleted successfully.", 
            type: "success" 
          });
          this.$router.push("/register");
        } catch (error) {
          this.$store.dispatch("showAlert", { 
            message: error.message || "Failed to delete account",
            type: "error" 
          });
        } finally {
          this.deletePassword = "";
          this.showDeleteInput = false;
          this.loading = false;
        }
        return;
      }

      // For email/password users, password is required
      if (!this.deletePassword) {
        this.$store.dispatch("showAlert", { 
          message: "Please enter your password to delete your account.", 
          type: "error" 
        });
        return;
      }

      this.loading = true;

      try {
        await deleteAccount(this.deletePassword);
        this.$store.dispatch("showAlert", { 
          message: "Account deleted successfully.", 
          type: "success" 
        });
        this.$router.push("/register");
      } catch (error) {
        this.$store.dispatch("showAlert", { 
          message: error.message == "Firebase: Error (auth/invalid-credential)." ? 'Incorrect password' : error.message,
          type: "error" 
        });
      } finally {
        this.deletePassword = "";
        this.showDeleteInput = false;
        this.loading = false;
      }
    },
    cancelDelete() {
      this.deletePassword = "";
      this.showDeleteInput = false;
    },
  },
};
</script>

<style scoped>
/* Custom styling for password toggle buttons */
button[type="button"] {
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
}
</style>